import React from 'react';
import './ProgressBar.css';

export type ProgressBarVariant = 'default' | 'success' | 'warning' | 'error';
export type ProgressBarSize = 'sm' | 'md' | 'lg';

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Current value (0-100 by default) */
  value: number;
  /** Maximum value (default: 100) */
  max?: number;
  /** Label text shown above the bar */
  label?: string;
  /** Show the value text */
  showValue?: boolean;
  /** Custom value formatter */
  formatValue?: (value: number, max: number) => string;
  /** Visual variant */
  variant?: ProgressBarVariant;
  /** Size of the bar */
  size?: ProgressBarSize;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showValue = false,
  formatValue,
  variant = 'default',
  size = 'md',
  className = '',
  ...rest
}) => {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const classes = [
    'hi-ui-progress-bar',
    `hi-ui-progress-bar--${variant}`,
    `hi-ui-progress-bar--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const valueText = formatValue ? formatValue(value, max) : `${Math.round(percentage)}%`;

  return (
    <div className={classes} {...rest}>
      {(label || showValue) && (
        <div className="hi-ui-progress-bar__header">
          {label && <span className="hi-ui-progress-bar__label">{label}</span>}
          {showValue && <span className="hi-ui-progress-bar__value">{valueText}</span>}
        </div>
      )}
      <div
        className="hi-ui-progress-bar__track"
        role="progressbar"
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-label={label}
      >
        <div className="hi-ui-progress-bar__fill" style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};
